// src/components/NearbyLocations.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const LocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const LocationCategory = styled.div`
  border: 1px solid var(--bdr);
  border-radius: 16px;
  background: var(--card);
  box-shadow: var(--shadow);
  padding: 20px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
  }
`;

const LocationsList = styled.div`
  display: grid;
  gap: 12px;
`;

const LocationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--bdr);
  
  &:last-child {
    border-bottom: none;
  }
  
  .name {
    font-weight: 600;
    color: var(--fg);
  }
  
  .distance {
    color: var(--muted);
    font-size: 14px;
    font-weight: 500;
  }
`;

const Description = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(176, 141, 87, 0.05);
  border-radius: 12px;
  border-left: 4px solid var(--primary);
  
  p {
    margin: 0;
    line-height: 1.6;
    color: var(--fg);
  }
`;

// Тестовые данные для демонстрации
const defaultLocations = {
  restaurants: [
    { name: 'Nobu Dubai', name_en: 'Nobu Dubai', name_ru: 'Nobu Dubai', name_zh: 'Nobu迪拜', distance: '0.5 км' },
    { name: 'Zuma', name_en: 'Zuma', name_ru: 'Zuma', name_zh: 'Zuma餐厅', distance: '0.8 км' },
    { name: 'La Petite Maison', name_en: 'La Petite Maison', name_ru: 'La Petite Maison', name_zh: '小房子餐厅', distance: '1.2 км' },
    { name: 'Pierchic', name_en: 'Pierchic', name_ru: 'Pierchic', name_zh: 'Pierchic餐厅', distance: '1.5 км' },
  ],
  shops: [
    { name: 'Dubai Mall', name_en: 'Dubai Mall', name_ru: 'Dubai Mall', name_zh: '迪拜购物中心', distance: '2.1 км' },
    { name: 'Mall of the Emirates', name_en: 'Mall of the Emirates', name_ru: 'Mall of the Emirates', name_zh: '阿联酋购物中心', distance: '3.5 км' },
    { name: 'City Walk', name_en: 'City Walk', name_ru: 'City Walk', name_zh: '城市步行街', distance: '1.8 км' },
    { name: 'Souk Madinat Jumeirah', name_en: 'Souk Madinat Jumeirah', name_ru: 'Souk Madinat Jumeirah', name_zh: '朱美拉古城集市', distance: '2.3 км' },
  ],
  infrastructure: [
    { name: 'Метро Emirates Towers', name_en: 'Emirates Towers Metro', name_ru: 'Метро Emirates Towers', name_zh: '酋长塔地铁站', distance: '0.3 км' },
    { name: 'Больница American Hospital', name_en: 'American Hospital', name_ru: 'Больница American Hospital', name_zh: '美国医院', distance: '1.1 км' },
    { name: 'Школа GEMS Wellington', name_en: 'GEMS Wellington School', name_ru: 'Школа GEMS Wellington', name_zh: 'GEMS惠灵顿学校', distance: '2.0 км' },
    { name: 'Аэропорт DXB', name_en: 'DXB Airport', name_ru: 'Аэропорт DXB', name_zh: '迪拜国际机场', distance: '12 км' },
  ],
};

export default function NearbyLocations({ 
  locations = defaultLocations, 
  description = null,
}) {
  const { t, i18n } = useTranslation();
  
  // Use translation if no description provided
  const displayDescription = description || (
    i18n.language === 'en' 
      ? "The property is located in a prestigious area with developed infrastructure. Within walking distance are world-class restaurants, shopping centers and major transport hubs."
      : i18n.language === 'zh'
      ? "该物业位于基础设施发达的著名地区。步行即可到达世界一流的餐厅、购物中心和主要交通枢纽。"
      : "Объект расположен в престижном районе с развитой инфраструктурой. В пешей доступности находятся рестораны мирового класса, торговые центры и основные транспортные узлы."
  );
  
  const categories = React.useMemo(() => [
    { key: 'restaurants', title: t('objectCard.nearbyLocations.restaurants') },
    { key: 'shops', title: t('objectCard.nearbyLocations.shops') },
    { key: 'infrastructure', title: t('objectCard.nearbyLocations.infrastructure') },
  ], [t]);

  // Helper to get location name based on language
  const getLocationName = (location) => {
    const lang = i18n.language || 'ru';
    if (lang === 'zh') {
      return location.name_zh || location.name_en || location.name_ru || location.name || '';
    }
    if (lang === 'en') {
      return location.name_en || location.name_ru || location.name || '';
    }
    return location.name_ru || location.name || '';
  };

  return (
    <div>
      {displayDescription && (
        <Description>
          <p>{displayDescription}</p>
        </Description>
      )}
      
      <LocationsGrid>
        {categories.map(category => {
          const categoryLocations = locations[category.key] || [];
          
          if (!categoryLocations.length) {
            return null;
          }
          
          return (
            <LocationCategory key={category.key}>
              <CategoryHeader>
                <h3>{category.title}</h3>
              </CategoryHeader>
              
              <LocationsList>
                {categoryLocations.map((location, index) => (
                  <LocationItem key={index}>
                    <span className="name">{getLocationName(location)}</span>
                    <span className="distance">{location.distance}</span>
                  </LocationItem>
                ))}
              </LocationsList>
            </LocationCategory>
          );
        })}
      </LocationsGrid>
    </div>
  );
}